import { FIN_CATEGORY_BY_KEY, type FinCategoryKey } from '#dictionary'
import { parseRegion, type Region } from '#dictionary/regions'
import { ValuePeriod } from '#domain/types'
import type MemberService from '#profile/MemberService'
import type { SubmissionAnswers, SubmissionDoc } from '#submissions'

export type SubmissionInput = Pick<SubmissionDoc, 'id'> & {
  entity: Pick<SubmissionDoc['entity'], 'topicId' | 'answers'>
}
import { getPeriodAnswerKey } from '#submissions'
import type { FinRecordBuilder, ISurveyCollection } from '#survey'
import { evaluateExpression } from '#survey/evaluator'
import {
  calculateQuestionsVisibility,
  isSubtopicVisible,
} from '#survey/questionsVisibility'
import {
  initSurveyVariables,
  type SurveyVariables,
  type SurveyVariablesProfileInput,
} from '#survey/surveyVariables'
import { parseZipCode } from '#zipCode'

import type { MemberRetirementAges } from '../memberRetirementAges'
import createFinancialRecord from './createFinancialRecord'
import type { FinancialRecordPayload } from './FinancialRecord'
import type { FinancialTreeNode } from './FinancialTreeNode'
import generateId from './generateId'
import { countRecordsByGroup } from './recordsByGroup'
import resolveRecordDates from './resolveRecordDates'
import type { SubtopicRecord } from './SubtopicRecord'
import { toDateOrUndefined } from './toDateOrUndefined'
import { toAnnualValue } from './valuePeriodMultiplier'

export type BuildContext = {
  surveyCollection: ISurveyCollection
  members: ReadonlyArray<MemberService>
  surveyVariablesInput: SurveyVariablesProfileInput
  retirementAges: MemberRetirementAges
}

export type FinancialRecordsBuild = {
  tree: FinancialTreeNode | null
  errors: string[]
  // Ids of subtopics whose renderIf currently evaluates truthy. Drives the
  // add-subtopic picker so hidden subtopics are not offered.
  visibleSubtopicIds: string[]
}

type EvaluateExpressionType<T> = string | undefined | T

const defaultValues = {
  valuePart: 1,
  name: '',
  value: 0,
  valuePeriod: ValuePeriod.YEARLY,
}

type BuildState = {
  context: BuildContext
  surveyVariables: SurveyVariables
  visibleAnswers: SubmissionAnswers
  errors: string[]
  questId: string | null
}

export type BuildFinancialRecordsOptions = {
  // Stamps every produced record with this id. Callers building records from
  // a quest submission pass questDoc.id here so downstream (chart pipeline,
  // Calculator's enabledQuestIds filter) can distinguish quest-derived records
  // from submission-derived ones. Undefined → records get questId: null.
  questId?: string
}

const evaluate = <T>(
  state: BuildState,
  expression: EvaluateExpressionType<T>
): T | undefined => {
  if (typeof expression !== 'string') {
    return expression as T
  }

  const isExpression = expression.startsWith('{') && expression.endsWith('}')
  if (!isExpression) {
    return expression as T
  }

  const expressionWithoutBraces = expression.slice(1, -1)
  return evaluateExpression<T>(expressionWithoutBraces, {
    answers: state.visibleAnswers,
    vars: state.surveyVariables,
  })
}

const resolveDateField = (
  state: BuildState,
  dateField: string | undefined
): Date | undefined => {
  const dateValue = evaluate<unknown>(state, dateField)
  return toDateOrUndefined(dateValue)
}

const resolveValuePeriod = (valuePeriod: string | undefined): ValuePeriod =>
  (valuePeriod as ValuePeriod) || ValuePeriod.YEARLY

const resolveOwner = (
  state: BuildState,
  owner: string | undefined
): MemberService => {
  const ownerId = evaluate<string>(state, owner)
  const ownerMember = state.context.members.find(m => m.id === ownerId)
  if (!ownerMember) {
    // The first member is the primary person (ProfileService.allMembers ordering).
    return state.context.members[0]
  }
  return ownerMember
}

const createNodeFromBuilder = (
  state: BuildState,
  builder: FinRecordBuilder,
  topicId: string,
  submissionId: string,
  parentId?: string,
  childIndex: number = 0
): FinancialTreeNode | null => {
  let shouldCreateRecord: boolean
  if (builder.if === undefined) {
    shouldCreateRecord = true
  } else {
    shouldCreateRecord = evaluate<boolean>(state, builder.if) ?? false
  }
  if (!shouldCreateRecord) {
    return null
  }

  const categoryKeyValue = evaluate<string>(state, builder.categoryKey)

  if (!categoryKeyValue) {
    const message = `Category key could not be resolved for record in form "${topicId}" (builder.categoryKey: ${JSON.stringify(builder.categoryKey)})`
    state.errors.push(message)
    return null
  }

  const categoryKeyString = String(categoryKeyValue)
  const category = FIN_CATEGORY_BY_KEY.get(categoryKeyString as FinCategoryKey)
  if (!category) {
    const message = `Category with key "${categoryKeyString}" not found in form "${topicId}"`
    state.errors.push(message)
    return null
  }

  const recordValue = evaluate<number | null>(state, builder.value)
  const value = recordValue ?? defaultValues.value ?? 0

  const owner = resolveOwner(state, builder.owner)

  const valuePeriod = evaluate<ValuePeriod>(state, builder.valuePeriod)

  const finalValuePeriod =
    resolveValuePeriod(valuePeriod) ?? defaultValues.valuePeriod

  const durationInPeriods = evaluate<number | null>(
    state,
    builder.durationInPeriods
  )

  const startAtDate = resolveDateField(state, builder.startAtDate) || null
  const startAtAge = evaluate<number | null>(state, builder.startAtAge)
  const endAtDate = resolveDateField(state, builder.endAtDate) || null
  const endAtAge = evaluate<number | null>(state, builder.endAtAge)
  const recordName = evaluate<string | undefined>(state, builder.name)
  const customGrowthRate = evaluate<number | null>(
    state,
    builder.customGrowthRate
  )
  const realEstateZipCode = evaluate<string | undefined>(
    state,
    builder.realEstateZipCode
  )
  // realEstateZipCode is optional — user may not set a per-record override.
  // But if they DID set one, it should parse — otherwise the record silently
  // uses owner region, masking the input error. Throw instead so ZL-1021's
  // UI-level handling can surface it as a per-record inline error.
  let realEstateRegion: Region | null = null
  if (realEstateZipCode) {
    realEstateRegion = parseRegion(parseZipCode(realEstateZipCode)?.state)
    if (!realEstateRegion) {
      throw new Error(
        `buildFinancialRecords: cannot resolve region from realEstateZipCode ` +
          `"${realEstateZipCode}". Expected a valid US zipCode. See ZL-1021.`
      )
    }
  }
  const realEstateTaxRate = evaluate<number | null>(
    state,
    builder.realEstateTaxRate
  )
  const recurringValue = evaluate<number | null>(state, builder.recurringValue)

  const resolvedValuePeriod = (finalValuePeriod ??
    defaultValues.valuePeriod) as ValuePeriod

  const { startAt, endAt } = resolveRecordDates(
    {
      startAtDate,
      startAtAge: startAtAge ?? null,
      endAtDate,
      endAtAge: endAtAge ?? null,
      durationInPeriods: durationInPeriods ?? null,
      valuePeriod: resolvedValuePeriod,
    },
    {
      owner,
      finCategory: category,
      retirementAges: state.context.retirementAges,
    }
  )

  const finData: FinancialRecordPayload = {
    id: generateId(submissionId, parentId ?? null, childIndex),
    name: recordName ?? defaultValues.name ?? '',
    finCategoryKey: category.key,
    annualValue: toAnnualValue(value, resolvedValuePeriod),
    valuePart: defaultValues.valuePart ?? 1,
    startAt,
    endAt,
    customRate: customGrowthRate ?? null,
    parentId: parentId ?? null,
    realEstateRegion,
    realEstateTaxRate: realEstateTaxRate ?? null,
    recurringValue: recurringValue ?? null,
    ownerId: owner.id,
    questId: state.questId,
    t_fromAssetId: null,
    t_toAssetId: null,
  }

  const financialRecord = createFinancialRecord(finData, category, owner)

  return {
    financialRecord,
    children: [],
  }
}

const buildFinRecordTree = (
  state: BuildState,
  builder: FinRecordBuilder,
  topicId: string,
  submissionId: string,
  parentId?: string,
  childIndex: number = 0
): FinancialTreeNode | null => {
  const node = createNodeFromBuilder(
    state,
    builder,
    topicId,
    submissionId,
    parentId,
    childIndex
  )

  if (!node) {
    return null
  }

  if (builder.children) {
    const children = builder.children.map((child, index) =>
      buildFinRecordTree(
        state,
        child,
        topicId,
        submissionId,
        node.financialRecord.id,
        index
      )
    )

    node.children = children.filter(child => child !== null)
  }

  return node
}

export const buildFinancialRecords = (
  context: BuildContext,
  submissionDoc: SubmissionInput,
  opts: BuildFinancialRecordsOptions = {}
): FinancialRecordsBuild => {
  const state: BuildState = {
    context,
    surveyVariables: initSurveyVariables(
      context.surveyVariablesInput,
      context.retirementAges
    ),
    visibleAnswers: {},
    errors: [],
    questId: opts.questId ?? null,
  }

  const { id: submissionId, entity: submission } = submissionDoc
  const { topicId, answers } = submission

  const topic = context.surveyCollection.getTopicById(topicId)

  if (!topic) {
    return {
      tree: null,
      errors: [`Topic not found for submission ${submissionId}`],
      visibleSubtopicIds: [],
    }
  }

  // A subtopic's own renderIf gates the subtopic as a whole. Evaluate it against
  // the full submission answers + vars: question ids are unique across the topic
  // tree, so a condition may reference topic-level answers OR another subtopic's
  // answers (e.g. a mutually-exclusive plan hidden when a sibling has a balance).
  // A hidden subtopic is excluded from the picker (via visibleSubtopicIds) and
  // from finRecord generation below.
  const visibleSubtopicIds = topic.subtopics
    .filter(subtopic =>
      isSubtopicVisible(subtopic, answers, state.surveyVariables)
    )
    .map(subtopic => subtopic.id)
  const visibleSubtopicIdSet = new Set(visibleSubtopicIds)

  const questions = context.surveyCollection.getQuestionsForTopic(topic)
  const visibility = calculateQuestionsVisibility(
    questions,
    answers,
    state.surveyVariables
  )

  // Answers of a visible subtopic are always lifted: a subtopic submission is a
  // discrete user action, so its answers must reach finRecordBuilder regardless
  // of any question-level renderIf that might evaluate falsy at adapt time
  // (e.g., an unrelated topic-level toggle still at its default).
  const subtopicQuestionIds = new Set(
    topic.subtopics
      .filter(subtopic => visibleSubtopicIdSet.has(subtopic.id))
      .flatMap(subtopic =>
        subtopic.sections.flatMap(s => s.questions.map(q => q.id))
      )
  )

  // A subtopic hidden by its own renderIf is excluded entirely: its questions are
  // dropped even though the visibility map (which spans subtopic questions too)
  // would otherwise mark them visible, so its answers never reach finRecordBuilder.
  const hiddenSubtopicQuestionIds = new Set(
    topic.subtopics
      .filter(subtopic => !visibleSubtopicIdSet.has(subtopic.id))
      .flatMap(subtopic =>
        subtopic.sections.flatMap(s => s.questions.map(q => q.id))
      )
  )

  Object.entries(answers).forEach(([questionId, value]) => {
    if (hiddenSubtopicQuestionIds.has(questionId)) {
      return
    }
    if (
      visibility.get(questionId) === true ||
      subtopicQuestionIds.has(questionId)
    ) {
      state.visibleAnswers[questionId] = value
    }
  })

  // A moneyWithPeriod question stores the raw amount under its id and the chosen
  // unit under `<id>Period`. Annualize the amount in-place here so every
  // finRecordBuilder expression reading this answer transparently sees the
  // annual figure (no helper, no expression edits). The period falls back to the
  // question's defaultPeriod (always set) — which is what lets legacy
  // submissions with no period answer read exactly as before, no migration.
  questions.forEach(question => {
    if (question.type !== 'moneyWithPeriod') return
    if (!(question.id in state.visibleAnswers)) return
    const periodKey = getPeriodAnswerKey(question.id)
    const period =
      (answers[periodKey] as ValuePeriod | undefined) ?? question.defaultPeriod
    state.visibleAnswers[periodKey] = period
    const value = state.visibleAnswers[question.id]
    if (typeof value === 'number') {
      state.visibleAnswers[question.id] = toAnnualValue(value, period)
    }
  })

  const rootNode = createNodeFromBuilder(
    state,
    topic.finRecordBuilder,
    topic.id,
    submissionId
  )

  if (!rootNode) {
    return {
      tree: null,
      errors: state.errors,
      visibleSubtopicIds,
    }
  }

  const sourceLevel1 = topic.finRecordBuilder.children ?? []
  const producedLevel1 = sourceLevel1.flatMap((builder, index) => {
    const subtree = buildFinRecordTree(
      state,
      builder,
      topic.id,
      submissionId,
      rootNode.financialRecord.id,
      index
    )
    return subtree ? [{ builder, tree: subtree }] : []
  })
  rootNode.children = producedLevel1.map(p => p.tree)

  const totalsBySubtopicId = new Map<string, number>()
  producedLevel1.forEach(({ builder, tree }) => {
    if (!builder.subtopicId) return
    const prev = totalsBySubtopicId.get(builder.subtopicId) ?? 0
    totalsBySubtopicId.set(
      builder.subtopicId,
      prev + tree.financialRecord.annualValue
    )
  })

  const subtopicRecords: SubtopicRecord[] = topic.subtopics.flatMap(
    subtopic => {
      if (!visibleSubtopicIdSet.has(subtopic.id)) return []
      const totalAnnualValue = totalsBySubtopicId.get(subtopic.id)
      if (totalAnnualValue === undefined) return []
      return [
        {
          id: subtopic.id,
          title: subtopic.title,
          shortTitle: subtopic.shortTitle,
          icon: subtopic.icon,
          totalAnnualValue,
        },
      ]
    }
  )

  if (subtopicRecords.length > 0) {
    rootNode.subtopicRecords = subtopicRecords
  }

  // Every level-1 child, minus those belonging to a subtopic the answers have
  // hidden. A child without a `subtopicId` still counts: it is a record the
  // topic produces directly, not an absence. Requiring one would empty this for
  // `spendLess`, whose single child is unbound and which declares no subtopics
  // at all, and would drop the tuition and room-and-board expenses that are the
  // substance of `collegeCosts`.
  //
  // Grandchildren stay out: the donor-advised fund's nested charity expense is
  // a consequence the engine derives, not something the user added, so counting
  // it would overstate what the topic contains.
  const recordsByGroup = countRecordsByGroup(
    producedLevel1
      .filter(
        ({ builder }) =>
          builder.subtopicId === undefined ||
          visibleSubtopicIdSet.has(builder.subtopicId)
      )
      .map(({ tree }) => tree.financialRecord)
  )

  if (Object.keys(recordsByGroup).length > 0) {
    rootNode.recordsByGroup = recordsByGroup
  }

  return {
    tree: rootNode,
    errors: state.errors,
    visibleSubtopicIds,
  }
}
